/**
 * macroGraph — Unreal-style MACROS for the Event Graph (collapsed sub-graphs
 * with exec + data tunnel pins).
 *
 * A `macro` node stores its body INLINE, like `graphGroup` does
 * (`data.innerNodes` / `data.innerEdges`), plus a signature:
 *
 *   data = { name, inputs: Pin[], outputs: Pin[], args?, innerNodes, innerEdges }
 *   Pin  = { id, name, type }   type ∈ MACRO_PIN_TYPES ('exec' included)
 *
 * Inside the body two locked tunnel nodes stand for the instance's pins:
 * `macroInput` (id MACRO_IN_ID) EMITS each input — an exec input becomes an
 * exec output there — and `macroOutput` (id MACRO_OUT_ID) RECEIVES each
 * output. Handle ids are keyed by the pin's stable `id`, never its name, so
 * renaming a pin keeps its wires:
 *
 *   instance  execIn:<id>  execOut:<id>  in-<id>  out-<id>
 *   macroInput            execOut:<id>           out-<id>
 *   macroOutput execIn:<id>                in-<id>
 *
 * Every exec handle starts with "exec", so graphConnectPolicy's one-wire
 * capacity rules and execReachability's edge filter apply unchanged.
 *
 * WHY MACROS SURVIVE A DELAY when functions do not: functions keep a GLOBAL
 * call stack (`_callStack` in EventRuntime), so a Delay inside a function
 * returns to the caller before the body finishes and the eventual Return pops
 * whatever frame happens to be on top. A macro instead runs its body in a
 * POOL that knows its own caller. The pool is a fresh nodes array per
 * (parent pool, instance id), and `contextOf(pool)` maps it back to
 * `{ macroNode, parentNodes, parentEdges }`. EventRuntime already threads
 * `(nodes, edges)` through every continuation — a Delay's setTimeout closes
 * over them — so the context rides along with any latent action for free,
 * and when the chain finally reaches the Outputs tunnel it resumes the
 * instance's exec output in the right parent pool. Nesting works the same
 * way: a nested macro's parent pool is the outer macro's pool array.
 *
 * Pools are CACHED (WeakMap on the parent nodes array, then by instance id)
 * and rebuilt only when the instance, its body or the parent's edges change,
 * so a macro on an onPointerDrag chain allocates nothing per fire. A pool is
 * a shallow copy of the body's nodes array — the copy is what makes the
 * context unique: two instances that happen to share one innerNodes array (a
 * shallow-copied duplicate) still get two pools and cannot resume each
 * other's outputs.
 *
 * Pure + import-light so it runs under plain `node --test`
 * (tests/unit/macroGraph.test.mjs), same reasoning as graphGroup.js.
 */
import { isExecHandle } from './graphConnectPolicy.js'
import { EVENT_NODE_TYPES } from './eventNodeTypes.js'

export const MACRO_TYPE = 'macro'
export const MACRO_INPUT_TYPE = 'macroInput'
export const MACRO_OUTPUT_TYPE = 'macroOutput'
export const MACRO_IN_ID = 'macro_in'
export const MACRO_OUT_ID = 'macro_out'

/** Pin types a macro signature may declare. `any` is a wildcard data pin: it
 *  passes whatever arrives and offers no literal box (what Collapse to Macro
 *  gives a data pin whose type it cannot know). */
export const MACRO_PIN_TYPES = ['exec', 'bool', 'int', 'float', 'string', 'any']

/**
 * Types a macro body may not contain.
 *  - Events: a macro is a piece of a chain, not a chain root. Its body is
 *    never scanned for starters (graphGroup.js only looks into groups), so an
 *    event inside would silently never fire.
 *  - Containers / tunnels / function scaffolding: the editor drills one
 *    level, and a tunnel copied into another body would have no instance.
 *  - Blueprint-wide singletons: their presence and teardown checks
 *    (EventRuntime.hasNodeOfType, App.jsx's watchers) do not look in macros.
 */
const FORBIDDEN_IN_MACRO = new Set([
  'graphGroup', MACRO_TYPE, MACRO_INPUT_TYPE, MACRO_OUTPUT_TYPE,
  'functionEntry', 'functionReturn',
  'voxyConfig', 'convorcherSession', 'headTrackedCamera',
])
export const isMacroTunnelType = type => type === MACRO_INPUT_TYPE || type === MACRO_OUTPUT_TYPE
export function isForbiddenInMacro(type) {
  return EVENT_NODE_TYPES.has(type) || FORBIDDEN_IN_MACRO.has(type)
}

// ── handle ids ───────────────────────────────────────────────────────────────
export const execInHandle  = id => `execIn:${id}`
export const execOutHandle = id => `execOut:${id}`
export const dataInHandle  = id => `in-${id}`
export const dataOutHandle = id => `out-${id}`

/** The pin id a macro/tunnel handle names, or null for any other handle. */
export function pinIdOfHandle(handle) {
  if (typeof handle !== 'string') return null
  if (handle.startsWith('execIn:'))  return handle.slice(7)
  if (handle.startsWith('execOut:')) return handle.slice(8)
  if (handle.startsWith('in-'))      return handle.slice(3)
  if (handle.startsWith('out-'))     return handle.slice(4)
  return null
}

/** A pin id not yet used in either list (`p1`, `p2`, …). */
export function nextPinId(inputs = [], outputs = []) {
  const used = new Set([...inputs, ...outputs].map(p => p.id))
  let n = 1
  while (used.has(`p${n}`)) n++
  return `p${n}`
}

/** `base`, or `base 2`, `base 3`… — the first not already a pin name in `pins`. */
export function uniquePinName(base, pins = []) {
  const taken = new Set(pins.map(p => p.name))
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base} ${n}`)) n++
  return `${base} ${n}`
}

/** The two locked tunnel nodes of a body. */
export function tunnelNodes({ inPos = { x: 60, y: 160 }, outPos = { x: 560, y: 160 } } = {}) {
  return [
    { id: MACRO_IN_ID,  type: MACRO_INPUT_TYPE,  position: inPos,  data: {}, deletable: false },
    { id: MACRO_OUT_ID, type: MACRO_OUTPUT_TYPE, position: outPos, data: {}, deletable: false },
  ]
}

/** Data for a fresh macro from the palette: one exec in, one exec out, wired
 *  straight through so an empty macro is a pass-through rather than a dead end. */
export function defaultMacroData() {
  return {
    name: 'Macro',
    inputs:  [{ id: 'p1', name: 'In',  type: 'exec' }],
    outputs: [{ id: 'p2', name: 'Out', type: 'exec' }],
    innerNodes: tunnelNodes(),
    innerEdges: [{
      id: 'me_pass', type: 'default',
      source: MACRO_IN_ID, sourceHandle: execOutHandle('p1'),
      target: MACRO_OUT_ID, targetHandle: execInHandle('p2'),
    }],
  }
}

/**
 * Edges a signature change leaves dangling: anything on the instance (in the
 * PARENT edges) or on a tunnel (in the INNER edges) whose pin no longer exists
 * or changed between exec and data. Returns the filtered arrays — the same
 * array when nothing was dropped, so a caller can skip a state update.
 */
export function prunePinEdges(macroNodeId, data, parentEdges = [], innerEdges = []) {
  const ins  = new Map((data.inputs  ?? []).map(p => [p.id, p]))
  const outs = new Map((data.outputs ?? []).map(p => [p.id, p]))
  const ok = (pins, handle, wantExec) => {
    const id = pinIdOfHandle(handle)
    const pin = id != null ? pins.get(id) : null
    return !!pin && (pin.type === 'exec') === wantExec && isExecHandle(handle) === wantExec
  }
  const keepParent = e => {
    if (e.target === macroNodeId && !ok(ins, e.targetHandle, isExecHandle(e.targetHandle))) return false
    if (e.source === macroNodeId && !ok(outs, e.sourceHandle, isExecHandle(e.sourceHandle))) return false
    return true
  }
  const keepInner = e => {
    if (e.source === MACRO_IN_ID && !ok(ins, e.sourceHandle, isExecHandle(e.sourceHandle))) return false
    if (e.target === MACRO_OUT_ID && !ok(outs, e.targetHandle, isExecHandle(e.targetHandle))) return false
    return true
  }
  const p = parentEdges.filter(keepParent)
  const i = innerEdges.filter(keepInner)
  return {
    parentEdges: p.length === parentEdges.length ? parentEdges : p,
    innerEdges:  i.length === innerEdges.length ? innerEdges : i,
  }
}

/**
 * A container body (macro or graphGroup) with fresh inner node ids, for
 * Duplicate. The runtime keys per-node state on the node id (rendezvous,
 * smoothDamp runs, function return values, clip players), so two instances
 * whose bodies share ids share that state: one instance's Smooth Damp cancels
 * the other's. The tunnels keep their reserved ids; the runtime finds them
 * by type and the editor by id.
 */
export function reidBody(innerNodes = [], innerEdges = [], mintId) {
  const map = new Map()
  for (const n of innerNodes) if (!isMacroTunnelType(n.type)) map.set(n.id, mintId())
  const to = id => map.get(id) ?? id
  return {
    innerNodes: innerNodes.map(n => (map.has(n.id) ? { ...n, id: to(n.id) } : n)),
    innerEdges: innerEdges.map(e => {
      const source = to(e.source), target = to(e.target)
      if (source === e.source && target === e.target) return e
      return { ...e, id: `e-${source}.${e.sourceHandle ?? ''}-${target}.${e.targetHandle ?? ''}`, source, target }
    }),
  }
}

// ── runtime pools ────────────────────────────────────────────────────────────
const EMPTY = Object.freeze([])

/**
 * A pool registry. EventRuntime holds one; tests make their own.
 *   poolFor(macroNode, parentNodes, parentEdges) → { nodes, edges, inNode, outNode, macroNode, parentNodes, parentEdges }
 *   contextOf(nodes) → that same entry when `nodes` is a macro pool, else null
 */
export function createMacroPools() {
  let byParent = new WeakMap() // parentNodes[] -> Map<macroId, entry>
  let byPool = new WeakMap()   // pool nodes[] -> entry

  function poolFor(macroNode, parentNodes, parentEdges) {
    let per = byParent.get(parentNodes)
    if (!per) { per = new Map(); byParent.set(parentNodes, per) }
    const defNodes = macroNode.data?.innerNodes ?? EMPTY
    const defEdges = macroNode.data?.innerEdges ?? EMPTY
    let entry = per.get(macroNode.id)
    if (!entry || entry.macroNode !== macroNode || entry.defNodes !== defNodes
        || entry.defEdges !== defEdges || entry.parentEdges !== parentEdges) {
      const nodes = defNodes.slice()
      entry = {
        macroNode, parentNodes, parentEdges, defNodes, defEdges,
        nodes, edges: defEdges,
        inNode:  nodes.find(n => n.type === MACRO_INPUT_TYPE) ?? null,
        outNode: nodes.find(n => n.type === MACRO_OUTPUT_TYPE) ?? null,
      }
      per.set(macroNode.id, entry)
      byPool.set(nodes, entry)
    }
    return entry
  }

  return {
    poolFor,
    contextOf: nodes => byPool.get(nodes) ?? null,
    reset() { byParent = new WeakMap(); byPool = new WeakMap() },
  }
}

// ── Collapse to Macro ────────────────────────────────────────────────────────
const NODE_W = 200

function pinNameFromHandle(handle, fallback) {
  if (typeof handle !== 'string' || !handle) return fallback
  if (handle === 'execIn' || handle === 'execOut') return fallback
  let s = handle
  if (s.startsWith('execOut')) s = s.slice(7)
  else if (s.startsWith('execIn')) s = s.slice(6)
  else if (s.startsWith('exec')) s = s.slice(4)
  else if (s.startsWith('out-')) s = s.slice(4)
  else if (s.startsWith('in-')) s = s.slice(3)
  s = s.replace(/^[:_-]+/, '')
  return s || fallback
}

/**
 * Move the selected nodes of one pool into a new macro (Unreal's "Collapse to
 * Macro"). Every wire that crossed the selection boundary becomes a tunnel
 * pin, so the graph behaves exactly as before:
 *  - an exec wire INTO the selection → one exec input per inner target pin
 *    (exec inputs hold one wire, so there is no fan-in to merge);
 *  - a data wire INTO the selection → one `any` input per OUTER source pin,
 *    fanned out inside to every inner target it fed;
 *  - an exec wire OUT → one exec output per inner source pin;
 *  - a data wire OUT → one `any` output per inner source pin, fanned out
 *    outside.
 *
 * Pure. `mintId()` mints node ids, `mintEdgeId()` edge ids.
 * @returns {{ nodes, edges, macroNode } | { error: string }}
 */
export function collapseToMacro(nodes, edges, selectedIds, { mintId, mintEdgeId, name = 'Macro' } = {}) {
  const sel = new Set(selectedIds)
  const picked = nodes.filter(n => sel.has(n.id))
  if (picked.length === 0) return { error: 'Select the nodes to collapse first.' }
  const bad = picked.filter(n => isForbiddenInMacro(n.type))
  if (bad.length) {
    const types = [...new Set(bad.map(n => n.type))].join(', ')
    return { error: `A macro cannot contain ${types} — leave ${bad.length === 1 ? 'it' : 'them'} outside the selection.` }
  }

  const macroId = mintId()
  const inputs = [], outputs = []
  const innerEdges = [], outerEdges = []
  const inKeyToPin = new Map(), outKeyToPin = new Map()
  const addPin = (list, key, map, name, type) => {
    let pin = map.get(key)
    if (!pin) {
      pin = { id: nextPinId(inputs, outputs), name: uniquePinName(name, list), type }
      list.push(pin); map.set(key, pin)
      return { pin, fresh: true }
    }
    return { pin, fresh: false }
  }

  for (const e of edges) {
    const sIn = sel.has(e.source), tIn = sel.has(e.target)
    if (sIn && tIn) { innerEdges.push(e); continue }
    if (!sIn && !tIn) { outerEdges.push(e); continue }
    const exec = isExecHandle(e.targetHandle) || isExecHandle(e.sourceHandle)
    if (!sIn && tIn) {
      if (exec) {
        const { pin } = addPin(inputs, `x|${e.target}|${e.targetHandle}`, inKeyToPin, pinNameFromHandle(e.targetHandle, 'In'), 'exec')
        outerEdges.push({ ...e, id: mintEdgeId(), target: macroId, targetHandle: execInHandle(pin.id) })
        innerEdges.push({ id: mintEdgeId(), type: e.type ?? 'default', source: MACRO_IN_ID, sourceHandle: execOutHandle(pin.id), target: e.target, targetHandle: e.targetHandle })
      } else {
        const { pin, fresh } = addPin(inputs, `d|${e.source}|${e.sourceHandle}`, inKeyToPin, pinNameFromHandle(e.targetHandle, 'value'), 'any')
        if (fresh) outerEdges.push({ ...e, id: mintEdgeId(), target: macroId, targetHandle: dataInHandle(pin.id) })
        innerEdges.push({ id: mintEdgeId(), type: e.type ?? 'default', source: MACRO_IN_ID, sourceHandle: dataOutHandle(pin.id), target: e.target, targetHandle: e.targetHandle })
      }
    } else {
      if (exec) {
        const { pin, fresh } = addPin(outputs, `x|${e.source}|${e.sourceHandle}`, outKeyToPin, pinNameFromHandle(e.sourceHandle, 'Out'), 'exec')
        if (fresh) innerEdges.push({ id: mintEdgeId(), type: e.type ?? 'default', source: e.source, sourceHandle: e.sourceHandle, target: MACRO_OUT_ID, targetHandle: execInHandle(pin.id) })
        outerEdges.push({ ...e, id: mintEdgeId(), source: macroId, sourceHandle: execOutHandle(pin.id) })
      } else {
        const { pin, fresh } = addPin(outputs, `d|${e.source}|${e.sourceHandle}`, outKeyToPin, pinNameFromHandle(e.sourceHandle, 'value'), 'any')
        if (fresh) innerEdges.push({ id: mintEdgeId(), type: e.type ?? 'default', source: e.source, sourceHandle: e.sourceHandle, target: MACRO_OUT_ID, targetHandle: dataInHandle(pin.id) })
        outerEdges.push({ ...e, id: mintEdgeId(), source: macroId, sourceHandle: dataOutHandle(pin.id) })
      }
    }
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const n of picked) {
    const x = n.position?.x ?? 0, y = n.position?.y ?? 0
    minX = Math.min(minX, x); minY = Math.min(minY, y)
    maxX = Math.max(maxX, x + (n.measured?.width ?? n.width ?? NODE_W))
    maxY = Math.max(maxY, y + (n.measured?.height ?? n.height ?? 80))
  }
  const midY = (minY + maxY) / 2
  const inner = picked.map(n => {
    const { selected: _s, dragging: _d, ...rest } = n
    return rest
  })
  const macroNode = {
    id: macroId, type: MACRO_TYPE,
    position: { x: minX, y: minY },
    data: {
      name, inputs, outputs,
      innerNodes: [...tunnelNodes({ inPos: { x: minX - 280, y: midY - 40 }, outPos: { x: maxX + 80, y: midY - 40 } }), ...inner],
      innerEdges,
    },
    selected: true,
  }
  return {
    nodes: [...nodes.filter(n => !sel.has(n.id)).map(n => (n.selected ? { ...n, selected: false } : n)), macroNode],
    edges: outerEdges,
    macroNode,
  }
}
